import type {
  PhaseConformityEvidence,
  PhaseFileEvidence,
  SkillInvocation,
} from './worker-protocol';
import {
  extractMarkdownHeadings,
  hasPhaseArtifactTitle,
  matchesNormalizedAlias,
  normalizeHeadingForMatch,
  normalizeMarkdownHeading,
  type PhaseArtifactKind,
} from './phase-conformity-heading-match';

export type { PhaseArtifactKind };

// v3 (iniciativa conformidade-templates-alinhados): ignora numeração de seção,
// aceita alias multi-palavra contido no heading, prefixos de título adicionais
// (`Tech Spec`, `Especificação Técnica`, `Documento de Requisitos`) e os aliases
// `Necessidade(s) do Usuário` para `userNeed`. Ver phase-conformity-heading-match.
export const PHASE_ARTIFACT_ANCHOR_MAP_VERSION = 3 as const;

export const PHASE_CONFORMITY_REPAIR_REQUIRED_EVENT =
  'phase_conformity_repair_required' as const;

export const PHASE_CONFORMITY_REPAIR_UNAVAILABLE_EVENT =
  'phase_conformity_repair_unavailable' as const;

export interface PhaseConformityRepairRequiredPayload {
  content: string;
  phaseAttemptId: string;
  missingReasonCodes: string[];
  missingConcepts: string[];
}

export interface PhaseConformityRepairUnavailablePayload {
  phaseAttemptId: string;
  code: 'SESSION_RECOVERY_EXHAUSTED' | 'SESSION_RECOVERY_UNAVAILABLE';
}

export type SkillSignalProfile = 'distinct' | 'not-distinguishable';

export interface PhaseArtifactAnchorDefinition {
  concept: string;
  aliases: readonly string[];
}

export const PHASE_ARTIFACT_ANCHORS: Record<
  PhaseArtifactKind,
  readonly PhaseArtifactAnchorDefinition[]
> = {
  prd: [
    { concept: 'vision', aliases: ['Visão Geral', 'Overview'] },
    {
      concept: 'objectives',
      aliases: ['Objetivos', 'Objetivos e métricas', 'Goals', 'Objectives'],
    },
    {
      concept: 'userNeed',
      aliases: [
        'Histórias de Usuário',
        'User Stories',
        'Necessidade do Usuário',
        'Necessidades do Usuário',
      ],
    },
    {
      concept: 'requirements',
      aliases: [
        'Funcionalidades Principais',
        'Funcionalidades',
        'Requisitos',
        'Features',
        'Functional Requirements',
      ],
    },
    {
      concept: 'scopeBoundary',
      aliases: [
        'Fora de Escopo',
        'Não-objetivos',
        'Escopo',
        'Non-goals',
        'Out of Scope',
      ],
    },
  ],
  techspec: [
    {
      concept: 'summary',
      aliases: ['Resumo Executivo', 'Technical Overview', 'Executive Summary'],
    },
    {
      concept: 'architecture',
      aliases: ['Arquitetura do Sistema', 'Arquitetura', 'System Architecture'],
    },
    {
      concept: 'implementation',
      aliases: [
        'Design de Implementação',
        'Implementação',
        'Implementation Design',
      ],
    },
    {
      concept: 'tests',
      aliases: ['Abordagem de Testes', 'Testes', 'Test Approach', 'Testing'],
    },
    {
      concept: 'sequence',
      aliases: [
        'Sequenciamento de Desenvolvimento',
        'Sequenciamento',
        'Development Sequence',
        'Delivery Plan',
      ],
    },
  ],
  task: [
    { concept: 'purpose', aliases: ['Visão Geral', 'Overview'] },
    { concept: 'dependencies', aliases: ['Dependências', 'Dependencies'] },
    {
      concept: 'acceptance',
      aliases: [
        'Critérios de Sucesso',
        'Acceptance Criteria',
        'Success Criteria',
      ],
    },
    {
      concept: 'tests',
      aliases: ['Testes da Tarefa', 'Testes', 'Task Tests', 'Test Plan'],
    },
  ],
};

const PHASE_ARTIFACT_CONCEPTS = new Set([
  'title',
  ...Object.values(PHASE_ARTIFACT_ANCHORS).flatMap(definitions =>
    definitions.map(definition => definition.concept),
  ),
]);

export function sanitizePhaseArtifactConcepts(
  concepts: readonly string[],
): string[] {
  return [
    ...new Set(
      concepts.filter(concept => PHASE_ARTIFACT_CONCEPTS.has(concept)),
    ),
  ].sort();
}

export const PHASE_CONFORMITY_REASON_CODES = {
  SKILL_REQUIRED_UNAVAILABLE: 'SKILL_REQUIRED_UNAVAILABLE',
  SKILL_INVOCATION_NOT_OBSERVED: 'SKILL_INVOCATION_NOT_OBSERVED',
  SKILL_INVOCATION_MISMATCH: 'SKILL_INVOCATION_MISMATCH',
  APPROACH_NOT_PRESENTED: 'APPROACH_NOT_PRESENTED',
  APPROACH_NOT_SELECTED: 'APPROACH_NOT_SELECTED',
  ARTIFACT_REQUIRED_MISSING: 'ARTIFACT_REQUIRED_MISSING',
  ARTIFACT_PATH_INVALID: 'ARTIFACT_PATH_INVALID',
  ARTIFACT_TOO_LARGE: 'ARTIFACT_TOO_LARGE',
  ARTIFACT_ANCHORS_MISSING: 'ARTIFACT_ANCHORS_MISSING',
  TASK_PLAN_MISSING: 'TASK_PLAN_MISSING',
  QA_REPORT_MISSING: 'QA_REPORT_MISSING',
  PR_NOT_PERSISTED: 'PR_NOT_PERSISTED',
  PR_INCONSISTENT: 'PR_INCONSISTENT',
  WORKER_PHASE_CONFORMITY_UNSUPPORTED: 'WORKER_PHASE_CONFORMITY_UNSUPPORTED',
  /**
   * O worker anuncia a capability, mas a chamada `validate_phase_conformity`
   * FALHOU (timeout, oscilação de rede, erro do worker). Distinto de
   * `WORKER_PHASE_CONFORMITY_UNSUPPORTED`: aqui o worker está atualizado e a
   * orientação é revalidar, não reinstalar. Confundir os dois mandou usuário
   * com worker 1.0.29 investigar atualização à toa (atividade do incidente de
   * 2026-08-18).
   */
  WORKER_CONFORMITY_VALIDATION_FAILED: 'WORKER_CONFORMITY_VALIDATION_FAILED',
} as const;

export interface AnchorEvaluation {
  anchorsFound: string[];
  anchorsMissing: string[];
}

export { normalizeMarkdownHeading };

export function evaluatePhaseArtifactAnchors(
  kind: PhaseArtifactKind,
  markdown: string,
): AnchorEvaluation {
  const headings = extractMarkdownHeadings(markdown).map(
    normalizeHeadingForMatch,
  );
  const anchorsFound: string[] = [];
  const anchorsMissing: string[] = [];

  if (hasPhaseArtifactTitle(kind, markdown)) {
    anchorsFound.push('title');
  } else {
    anchorsMissing.push('title');
  }

  for (const definition of PHASE_ARTIFACT_ANCHORS[kind]) {
    const aliases = definition.aliases.map(normalizeMarkdownHeading);
    const found = headings.some(heading =>
      aliases.some(alias => matchesNormalizedAlias(heading, alias)),
    );
    (found ? anchorsFound : anchorsMissing).push(definition.concept);
  }

  return { anchorsFound, anchorsMissing };
}

export class PhaseConformityCollector {
  private skillLoaded = false;
  private skillInvocation: SkillInvocation;
  private presentedApproaches = false;
  private selectedApproach = false;
  private files: PhaseFileEvidence[] = [];
  private skillLoadedAt?: string;
  private skillInvokedAt?: string;
  private approachPresentedAt?: string;
  private approachSelectedAt?: string;
  private readonly reasonCodes = new Set<string>();

  constructor(
    private readonly phaseAttemptId: string,
    private readonly expectedSkill: string,
    private readonly signalProfile: SkillSignalProfile,
  ) {
    this.skillInvocation =
      signalProfile === 'distinct' ? 'not-observed' : 'not-distinguishable';
  }

  markSkillLoaded(at = new Date().toISOString()): void {
    this.skillLoaded = true;
    this.skillLoadedAt ??= at;
    this.reasonCodes.delete(
      PHASE_CONFORMITY_REASON_CODES.SKILL_REQUIRED_UNAVAILABLE,
    );
  }

  markSkillUnavailable(): void {
    this.skillLoaded = false;
    this.reasonCodes.add(
      PHASE_CONFORMITY_REASON_CODES.SKILL_REQUIRED_UNAVAILABLE,
    );
  }

  observeSkillInvocation(
    observedSkill: string,
    at = new Date().toISOString(),
  ): void {
    if (this.signalProfile === 'not-distinguishable') {
      return;
    }
    const expected = normalizeSkillName(this.expectedSkill);
    const observed = normalizeSkillName(observedSkill);
    this.skillInvocation =
      expected === observed ? 'observed-expected' : 'observed-other';
    this.skillInvokedAt ??= at;
  }

  markApproachPresented(at = new Date().toISOString()): void {
    this.presentedApproaches = true;
    this.approachPresentedAt ??= at;
  }

  markApproachSelected(at = new Date().toISOString()): void {
    this.selectedApproach = true;
    this.approachSelectedAt ??= at;
  }

  replaceFiles(files: PhaseFileEvidence[], reasonCodes: string[] = []): void {
    this.files = files.map(file => ({ ...file }));
    const validationReasonCodes = new Set<string>(
      Object.values(PHASE_CONFORMITY_REASON_CODES).filter(
        code =>
          code !== PHASE_CONFORMITY_REASON_CODES.SKILL_REQUIRED_UNAVAILABLE,
      ),
    );
    for (const code of this.reasonCodes) {
      if (validationReasonCodes.has(code)) {
        this.reasonCodes.delete(code);
      }
    }
    for (const code of reasonCodes) {
      this.reasonCodes.add(code);
    }
  }

  snapshot(): PhaseConformityEvidence {
    return {
      phaseAttemptId: this.phaseAttemptId,
      expectedSkill: this.expectedSkill,
      skillLoaded: this.skillLoaded,
      skillInvocation: this.skillInvocation,
      presentedApproaches: this.presentedApproaches,
      selectedApproach: this.selectedApproach,
      files: this.files.map(file => ({ ...file })),
      ...(this.skillLoadedAt ? { skillLoadedAt: this.skillLoadedAt } : {}),
      ...(this.skillInvokedAt ? { skillInvokedAt: this.skillInvokedAt } : {}),
      ...(this.approachPresentedAt
        ? { approachPresentedAt: this.approachPresentedAt }
        : {}),
      ...(this.approachSelectedAt
        ? { approachSelectedAt: this.approachSelectedAt }
        : {}),
      ...(this.reasonCodes.size > 0
        ? { reasonCodes: [...this.reasonCodes].sort() }
        : {}),
    };
  }
}

function normalizeSkillName(value: string): string {
  const bare = value.trim().replace(/^\//, '');
  const withoutCanonicalNamespace = bare.replace(/^spassu-code:/, '');
  return `/${withoutCanonicalNamespace}`;
}
